// Astra Lite's client (#2549, ADR 0023). POST /astra-lite on counselor-agent
// is the one auth-exempt route there: a visitor who has not signed in has no
// token, so this is a plain fetch (the route gates itself: the astra_lite
// flag or a signed preview token, a signed session token, per-IP caps and a
// global budget). The sign-in join is the exception and uses authedFetch.
import { authedFetch } from './api';
import { teamToken } from '../utils/teamDevice';

const BASE = import.meta.env.VITE_COUNSELOR_AGENT_URL
    || 'https://us-east1-college-counselling-478115.cloudfunctions.net/counselor-agent';
const URL_ = `${BASE}/astra-lite`;

// The founder's preview (flag off for everyone else): ?astra_preview=<token>
// on any landing URL. Kept for the tab's session; the server validates it on
// every call, so a stale or forged token only ever gets today's free read.
export const PREVIEW_PARAM = 'astra_preview';
const PREVIEW_KEY = 'astra_lite_preview';
export const HANDOFF_KEY = 'astra_lite_handoff';
export const PICTURE_KEY = 'try_hero_picture_anonymous';

// The grade the landing picture gave, as /welcome's card names it
// ('Freshman'..'Senior'), or null (#2945: a phone student who already told
// Astra their grade is not asked again). A read only: /profile adopts and
// clears the picture itself, as before.
const GRADE_CARDS = { 9: 'Freshman', 10: 'Sophomore', 11: 'Junior', 12: 'Senior' };
export function pictureGrade() {
    let raw = null;
    try { raw = JSON.parse(localStorage.getItem(PICTURE_KEY) || 'null')?.fields?.grade; } catch { return null; }
    if (raw === null || raw === undefined || typeof raw === 'boolean') return null;
    const s = String(raw).toLowerCase();
    const word = ['freshman', 'sophomore', 'junior', 'senior'].findIndex((w) => s.includes(w));
    if (word >= 0) return GRADE_CARDS[9 + word];
    const m = s.match(/\b(9|10|11|12)(?:st|nd|rd|th)?\b/);
    return m ? GRADE_CARDS[Number(m[1])] : null;
}

export function previewToken() {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get(PREVIEW_PARAM);
        if (fromUrl && /^p1\.\d+\.[A-Za-z0-9_-]{8,64}$/.test(fromUrl)) {
            sessionStorage.setItem(PREVIEW_KEY, fromUrl);
            // Off the address bar at once, so it never rides into history,
            // a shared link or an analytics page URL (security review).
            const url = new URL(window.location.href);
            url.searchParams.delete(PREVIEW_PARAM);
            window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
            return fromUrl;
        }
        return sessionStorage.getItem(PREVIEW_KEY) || null;
    } catch {
        return null;
    }
}

// The signed tokens a call carries: the founder's preview, and a team
// device's token (#3021), which the server re-verifies and uses only to file
// the conversation as source 'team' instead of 'visitor'.
function ownTokens() {
    const preview = previewToken();
    const team = teamToken();
    return { ...(preview ? { preview } : {}), ...(team ? { team } : {}), ...optOut(), ...windowShape() };
}

// The page's window and screen (#4620): [page width, screen width, screen
// height], the same three numbers PostHog records as $viewport_width,
// $screen_width and $screen_height. The server files a scripted window
// (astra_lite_analytics.SCRIPTED_WINDOWS, the PostHog clause's twin) as a
// machine: still answered, never counted as a visitor. Shape only; nothing
// else about the browser is sent.
export function windowShape() {
    // an opt-out browser (GPC / Do Not Track) sends nothing extra (security review of #4620)
    if (optOut().opt_out) return {};
    try {
        const w = [window.innerWidth, window.screen?.width, window.screen?.height];
        return w.every((n) => Number.isInteger(n) && n > 0) ? { win: w } : {};
    } catch {
        return {};
    }
}

// The browser's own opt-out (security review of #3079): Global Privacy
// Control and Do Not Track are set as headers by some browsers and only as
// navigator properties by others, so the page says so in the body too, and
// the server keeps nothing for either (astra_lite.opted_out).
export function optOut() {
    try {
        const nav = typeof navigator !== 'undefined' ? navigator : {};
        return (nav.globalPrivacyControl === true || nav.doNotTrack === '1' || nav.doNotTrack === 'yes')
            ? { opt_out: true } : {};
    } catch {
        return {};
    }
}

async function post(body) {
    let res;
    try {
        // authedFetch attaches the account's ID token only when someone is
        // signed in (the allowlisted founder dogfooding with the flag in
        // allowlist mode); a guest's call goes out exactly as before.
        res = await authedFetch(URL_, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...body, ...ownTokens() }),
        });
    } catch {
        return { success: false, status: 0 };
    }
    let data = {};
    try { data = await res.json(); } catch { /* an HTML error page */ }
    return { ...data, status: res.status, success: res.ok && data.success !== false };
}

export const astraStatus = () => post({ op: 'status' });
// The team link's check (#3021): is this token genuine? { team: true } only
// for a token the server signed. Sends the candidate, never the stored one.
export async function checkTeamToken(token) {
    try {
        const res = await fetch(URL_, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ op: 'team_device', team: token }),
        });
        const data = await res.json();
        return res.ok && data?.team === true;
    } catch {
        return false;
    }
}
// The server's cheap bot check (astra_lite.py MIN_MS_ON_PAGE, pinned by
// AstraPageTime.test.jsx): a picture, or a question that opens a new
// conversation, must come from a page open at least this long.
export const MIN_MS_ON_PAGE = 1200;
const LOADED_AT = Date.now();

// How long this PAGE has been open: performance.now() runs from the
// document's navigation start, so it holds across SPA route changes. Never
// time since a component mounted (#3307: the college page's sheet mounts
// AstraChat on the seed click itself, so its first ask sent ~0 ms and the
// server answered "Give it a second and try again").
export const msOnPage = () => {
    try { return Math.round(performance.now()); } catch { return Date.now() - LOADED_AT; }
};

// The page time to send with an ask. A person never sees the bot check's
// refusal: a click faster than the minimum waits out the remainder here
// first (the server's threshold is unchanged).
export async function pageTimeForAsk() {
    const t = msOnPage();
    if (t >= MIN_MS_ON_PAGE) return t;
    await new Promise((r) => setTimeout(r, MIN_MS_ON_PAGE - t + 50));
    return Math.max(msOnPage(), MIN_MS_ON_PAGE);
}

// `token`: the open chat's session ("Make it about me", #2725): the picture
// upgrades that same conversation and shares its day's count.
// `origin` (#3473): where this conversation started, one of ORIGINS; the
// server counts it once, only when the picture opens a new conversation.
export const astraPicture = (text, tMs, hp = '', token = null, origin = null) => post({
    op: 'picture', text, t_ms: tMs, hp, ...(token ? { token } : { origin: originOf(origin) }) });
// The GPA ask: the fields the session was signed over, plus the GPA alone.
export const astraRefine = (token, fields, gpa) => post({ op: 'refine', token, fields, gpa });
export const astraRead = (token, fields, schoolId) => post({ op: 'read', token, fields, school_id: schoolId });
// A sanity question answered: a choice id, or a number (never both).
export const astraClarify = (token, fields, check, answer) => post({ op: 'clarify', token, fields, check,
    ...(typeof answer === 'number' ? { number: answer } : { choice: answer }) });
export const astraAsk = (token, fields, message, history, hp = '') =>
    post({ op: 'ask', token, fields, message, history, hp });
export const astraStartFree = (token) => post({ op: 'start_free', token });

// The open chat (#2725): a question with no picture. The first question has
// no token; the server mints one (a random conversation id, signed over no
// picture) and every answer returns the next. The token is kept in this
// browser so the daily count holds across a reload; it carries no words.
export const CHAT_TOKEN_KEY = 'astra_chat_token';
// Where a conversation started (#3473), the server's closed list
// (astra_lite_analytics.ORIGINS): the landing page, a college page (its Ask
// box or its "see your odds" link), a /ask/<school> link from a Short, a
// News story. Sent only by the question or picture that opens a
// conversation (no token yet); anything else is 'other'.
export const ORIGINS = ['home', 'college_page', 'ask_link', 'news', 'other'];
export const originOf = (o) => (ORIGINS.includes(o) ? o : 'other');
// 'popular' (#5258): a chip from the "Most asked this week" row.
export const ENTRIES = ['typed', 'seed', 'chip', 'band', 'personal', 'popular'];
export function chatToken() {
    try { return localStorage.getItem(CHAT_TOKEN_KEY) || null; } catch { return null; }
}
export function keepChatToken(token) {
    try {
        if (token) localStorage.setItem(CHAT_TOKEN_KEY, token); else localStorage.removeItem(CHAT_TOKEN_KEY);
    } catch { /* private mode: the count then lives for the tab only */ }
}
// `tMs`: how long the page has been open (the bot check a question that
// opens a new conversation passes, like a picture).
export const astraChatAsk = (token, message, history, { entry = 'typed', tab = null, hp = '', tMs = 0, origin = null } = {}) => post({
    op: 'ask', message, history, hp, t_ms: tMs, entry: ENTRIES.includes(entry) ? entry : 'typed',
    ...(tab ? { tab } : {}), ...(token ? { token } : { origin: originOf(origin) }),
});

// The open chat, streamed (#2809): the same question to /astra-lite-stream,
// read as NDJSON. Each {"t": text} line is the next piece of the answer that
// has passed the server's guards; onText(textSoFar) shows it. The last line
// {"final": body, "status": code} is exactly what /astra-lite would have
// answered. Returns null when the stream route could not be reached at all
// (the caller then asks /astra-lite, once); a stream that broke after it
// started is a failure, never a second ask (the question may have counted).
export async function astraChatAskStream(token, message, history, opts = {}, onText = null) {
    const { entry = 'typed', tab = null, hp = '', tMs = 0, origin = null } = opts;
    const body = {
        op: 'ask', message, history, hp, t_ms: tMs, entry: ENTRIES.includes(entry) ? entry : 'typed', stream: true,
        ...(tab ? { tab } : {}), ...(token ? { token } : { origin: originOf(origin) }), ...ownTokens(),
    };
    let res;
    try {
        res = await authedFetch(`${BASE}/astra-lite-stream`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
    } catch {
        return null;
    }
    if (res.status === 404 || !res.body || typeof res.body.getReader !== 'function') return null;
    if (!res.ok) {
        let data = {};
        try { data = await res.json(); } catch { /* an HTML error page */ }
        return { ...data, status: res.status, success: false };
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let shown = '';
    let final = null;
    const take = (line) => {
        if (!line.trim()) return;
        let obj;
        try { obj = JSON.parse(line); } catch { return; }
        if (obj && typeof obj.t === 'string') {
            shown += obj.t;
            try { onText?.(shown); } catch { /* a render error never stops the read */ }
        } else if (obj && obj.final && typeof obj.final === 'object') {
            final = obj;
        }
    };
    try {
        for (;;) {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            let i = buf.indexOf('\n');
            while (i >= 0) {
                take(buf.slice(0, i));
                buf = buf.slice(i + 1);
                i = buf.indexOf('\n');
            }
        }
        take(buf);
    } catch {
        return { success: false, status: 0 };
    }
    if (!final) return { success: false, status: 0 };
    const status = Number.isInteger(final.status) ? final.status : 200;
    return { ...final.final, status, success: status < 400 && final.final.success !== false };
}

/**
 * After sign-in: the browser that pressed Start free tells the record it
 * signed in (one boolean; no identity is stored), then forgets the handoff.
 */
export async function reportAstraLiteJoin() {
    let handoff = null;
    try { handoff = localStorage.getItem(HANDOFF_KEY); } catch { return; }
    if (!handoff) return;
    try {
        await authedFetch(`${BASE}/astra-lite-joined`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ handoff }),
        });
    } catch { /* best effort */ }
    try { localStorage.removeItem(HANDOFF_KEY); } catch { /* private mode */ }
}
